// ─────────────────────────────────────────────────────
// 来源：BJT商务日语能力测试+模拟与对策.pdf
// 第1部聴解 / 第2部聴読解 / 第3部読解
// ─────────────────────────────────────────────────────

export const studyGuideData = {

  // ════════════════════════════════════════════════════
  // 第1部  聴解
  // ════════════════════════════════════════════════════
  7: {
    chapterTitle: '第1部聴解',
    coreQuestion: '在无法依赖文字和视觉辅助的纯听觉环境下，如何将连续的日语语音流，快速、准确地转化为结构化的商务信息，并理解其场景意图？',
    sections: [
      {
        id: 'B1',
        title: 'B1. 信息捕捉与场景理解',
        intro: {
          overview: '章前总览：在明确了BJT考试旨在解决"将语言知识转化为商务沟通能力"这一核心问题后，我们首先攻克最基础、也最具挑战性的环节——听力。本章的核心任务是：在无法依赖文字和视觉辅助的纯听觉环境下，如何将连续的日语语音流，快速、准确地转化为结构化的商务信息，并理解其场景意图。',
          highlight: '将语音流转化为结构化商务信息',
          logicChain: [
            'C1. 基础商务场景识别：首先，我们需要知道在商务环境中通常会听到什么（问候、介绍、预约等）。',
            'C2. 关键信息抓取术：其次，在特定场景的对话中，我们需要知道重点听什么、怎么抓（5W1H、数字、转折词）。',
            'C3. 场景化表达与意图理解：最后，透过表面的语言，理解说话人的真实目的和言外之意（请求、道歉、建议的真实意图）。',
          ],
        },
        subsections: [
          {
            id: 'C1',
            title: 'C1. 基础商务场景识别——构建听觉信息的结构框架',
            body: '商务日语听力中常见困境：每个单词都认识，整段话听完后对"谁、在哪儿、要干什么"一片模糊。原因是缺乏场景预判和信息结构意识。核心概念"基础商务场景"指在商务活动中高频出现、具有固定流程和常用表达的典型情境，包括：公司内外问候（挨拶・紹介）、电话应对与转接（電話応対）、来访接待与预约确认（受付・アポイントメント確認）、会议安排与通知（会議の手配・連絡）以及简单的业务说明与咨询（業務説明・相談）。',
            highlights: '一旦预判出场景，大脑会自动激活相关"知识模块"，听的时候就有了明确的信息捕捉目标，这能极大降低认知负荷。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '场景预判的价值与方法',
              points: [
                {
                  number: 1,
                  title: '抓住"场景启动词"——对话开头是钥匙',
                  content: '对话开头几句是场景的"钥匙"。听到「もしもし、山田商事でございます」立刻预判为"公司接电话"，后续关注来访者身份、事由、转接等信息。听到「では、明日の打ち合わせの件ですが…」立刻预判为"会议安排确认/变更"，重点关注时间、地点、议题的变动。',
                },
                {
                  number: 2,
                  title: '场景（C1）提供框架，意图（C3）填充内容',
                  content: '例如，在"电话转接"场景中，来电者的意图可能是"请求与XX负责人通话"。那么听力焦点应放在"Who（找谁）"和"Why（何事）"上。意图往往通过"组合拳"表达：先道歉（申し訳ありません）→ 解释原因（本日、担当者が外出しておりますので）→ 提出替代方案（折り返しお電話させましょうか）。',
                },
              ],
            },
          },
          {
            id: 'C2',
            title: 'C2. 关键信息抓取术——在语音流中精准"下锚"',
            body: '明确场景框架后，需要在对话"河流"中精准"下锚"，抓住决定答案的关键信息点。这些信息点与5W1H高度相关：Who（说话人、涉及人物）、When（时间、日期、期限）、Where（地点、场所、部门）、What（事件、物品、工作内容）、Why（原因、理由，常隐含在解释说明部分）、How（方法、手段、状态）。⚠易错点："听到什么选什么"陷阱——BJT常设置干扰信息，说话人先提方案A，再用「でも」转折提出方案B，答案往往是B。务必关注转折词后的内容。',
            highlights: '转折信号词：「しかし・でも・ただし・～が」后是重点；总结信号：「つまり・要するに」；语气信号：「実は…・あのう…」可能引出重要请求或补充。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '两大具体抓取技巧',
              points: [
                {
                  number: 1,
                  title: '技巧1：数字敏感度训练',
                  content: '听到日期、时间、金额、数量等数字立刻用草稿纸做简易记录（如：3/15，14:00，5万円）。注意日语数字单位（万/億）和时间读法（4時半、20分過ぎ）。问题可能需要计算："比原计划晚几天？""总计多少钱？"，听到多个相关数字时要明确它们之间的关系。',
                },
                {
                  number: 2,
                  title: '技巧2：转折词与语气词监听',
                  content: '「ので・から・ため・なぜなら」后面跟着原因/理由，可能是考点。「つまり・要するに・ですから」是对前面信息的概括，是核心结论。「実は…・あのう…」可能引出难以启齿的请求、道歉或重要补充。这些词是信息重点和说话人态度发生变化的"信号灯"。',
                },
              ],
            },
          },
          {
            id: 'C3',
            title: 'C3. 场景化表达与意图理解——听懂"弦外之音"',
            body: '商务交流讲究委婉和礼貌，直接命令或拒绝较少。能否理解场景化表达背后的真实意图，是区分"听懂字面"和"听懂内涵"的关键。常见意图与表达模式：①请求（依頼）：「～ていただけませんか・～をお願いできますか」；②建议（提案）：「～たらどうでしょうか・～はいかがですか」；③道歉与解释（謝罪と説明）：「申し訳ございません・大変失礼いたしました」，重点不在道歉本身，而在原因（Why）和解决方案（How）；④委婉拒绝：「あいにく…・せっかくですが…・～はちょっと…・検討させていただきます」。',
            highlights: '⚠最高频考点：听到「あいにく」「せっかくですが」「～はちょっと」「検討させていただきます」等"暧昧"表达时，其真实意图很可能是"拒绝"或"有困难"，需结合上下文确认。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '"意图-场景"联动分析思维',
              points: [
                {
                  number: 1,
                  title: '意图往往通过"组合拳"表达',
                  content: '一个人可能先道歉（申し訳ありません），然后解释原因（本日、担当者が外出しておりますので），最后提出替代方案或请求（明日の午前中に折り返しお電話させましょうか）。需要将碎片化表达与关键信息在场景框架中组合起来，才能完整还原说话人的逻辑和最终意图。',
                },
                {
                  number: 2,
                  title: '字面意思 vs. 社交含义',
                  content: '这是听力考试最大的陷阱。字面意思是"听懂每个词和语法结构"，社交含义是"理解在特定商务文化和语境下这句话实际传达的功能"（是拒绝、是催促、是表示为难还是真心夸奖）。应对策略：结合说话人身份、语气、前后逻辑进行综合推断。听到非常礼貌但内容空泛的表达时，要警惕其背后可能隐藏的负面意图。',
                },
              ],
            },
          },
        ],
      },
      {
        id: 'B2',
        title: 'B2. 视听信息整合与逻辑判断',
        intro: {
          overview: '章前总览：在掌握了纯听觉环境下捕捉信息的能力（B1）之后，本章将面对更贴近真实商务场景的复合信息环境。核心任务是：当信息同时来自耳朵（对话/说明）和眼睛（图表/文书/广告）时，如何高效整合、对比分析，并基于商务常识做出唯一正确的逻辑判断？',
          highlight: '将听到的说明与看到的视觉信息快速关联、交叉验证',
          logicChain: [
            '如何快速看懂视觉材料？-> (C4) 商务视觉材料解读术：掌握图表、广告、传单、日程表的快速阅读核心。',
            '如何将"听到的"和"看到的"联系起来？-> (C5) 信息交叉验证法：学会将听力说明与视觉数据进行对应、排除矛盾选项。',
          ],
        },
        subsections: [
          {
            id: 'C4',
            title: 'C4. 商务视觉材料解读术——图表、广告、传单、日程表',
            body: '在聴読解中，视觉材料是解题的"地图"。快速准确解读其结构和关键信息是节省时间、定位答案的基础。①柱状图：快速比较不同项目的数值大小，先看标题和纵轴单位，找出最大/最小值；②饼图：理解整体与部分的构成比例，看标题明确"整体"，找出占比最大/最小的部分；③折线图：分析数据随时间的变化趋势，关注起点、终点、拐点；④表格：关注"交叉点"与"条件"，先看清行/列标题代表什么维度，根据条件定位到交叉单元格；⑤广告/传单：关注"卖点"与限制条件，用★等符号标出的内容往往是核心；⑥日程表：纵向看资源，横向看时间，注意"已占用/空闲"（×/○）符号。',
            highlights: '⚠易错点：混淆比较基准（如数值是"相对于平均的百分比"而非实际小时数）；忽视单位（円/％/kg）；漏看备注或小字（※注释常包含重要限制条件）。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '各类材料的解读核心',
              points: [
                {
                  number: 1,
                  title: '图表（グラフ）示例辨析',
                  content: '"部門別残業時間"条形图：图中数值是百分比，超过100%即高于全社平均，而非实际小时数；"商品別売上構成比"表格：本质是多个饼图的年度对比，核心是追踪特定商品随时间的变化趋势；"利益率の変化"折线图：核心是判断哪条线在指定年份间呈现了特定变化模式（如"先下降后上升"）。',
                },
                {
                  number: 2,
                  title: '表格与日程表实战技巧',
                  content: '运费表（送先地域×重量）：根据听到的"送往哪里"和"重多少公斤"两个条件，锁定唯一价格。会议室预约表：根据"人数"和"希望时间段"，找到同时满足"容量足够"且"该时间段为空（○）"的会议室。个人日程表：根据"事件"或"约见对象"，在正确人物的正确时间格里找到匹配信息。',
                },
              ],
            },
          },
          {
            id: 'C5',
            title: 'C5. 信息交叉验证法——听力说明与图表数据的对应',
            body: '听力信息（对话或独白）是"指令"或"线索"，视觉材料是"数据库"或"地图"。解题过程是执行一次精准的"信息查询与验证"。标准解题流程（四步法）：①预读视觉材料——利用间隙快速浏览，理解主题、结构和关键数据点，预测问题方向；②聆听关键条件——捕捉问题核心及1-2个关键限定条件（对象、时间、数值、状态）；③执行交叉定位——将听到的条件作为"坐标"，在视觉材料中进行定位；④排除矛盾选项——逐一核对选项，但凡有一处矛盾即可排除。',
            highlights: '易错点：①主观臆断——所有判断必须严格基于听力信息和视觉数据，不能引入个人常识；②信息错配——确保将听力条件对应到图表的正确维度；③忽略否定和转折——「しかし・実は・～ない」会彻底改变条件，必须重点捕捉。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '排除法应用示例',
              points: [
                {
                  number: 1,
                  title: '交叉定位示例（运费表）',
                  content: '听力：「送り先は東北で、重さは15kgです。」 → 条件A：目的地="東北・関西"行；条件B：重量="15kg"列 → 交叉点"1,450円"即为答案。听力：「高橋さんは午後、UPR証券を訪問します。」 → 条件A：人物="高橋"；条件B：事件="UPR証券訪問" → 在"高橋"行找到对应时间格"14:00"即为答案。',
                },
                {
                  number: 2,
                  title: '推理判断示例（饼图题）',
                  content: '听力可能说"A営業所は、売上比率より消費量比率の方が高いです"。推理：对比两个饼图中A所的占比（売上比29% vs 消費量比22%），29%>22%与听力描述"消费量占比>销售额占比"矛盾，故A所不符合，需找到哪个所的"消費量占比>売上占比"。',
                },
              ],
            },
          },
        ],
      },
      {
        id: 'B3',
        title: 'B3. 商务文书理解与规则应用',
        intro: {
          overview: '章前总览：在掌握了从听觉（聴解）和视听结合（聴読解）中捕捉信息的能力后，本章将聚焦于纯粹的文本世界。核心任务是：如何准确、高效地理解正式商务文书（如邮件、报告、合同），并运用其中的商业规则和礼仪，做出正确的判断和回应？',
          highlight: '攻克商务文书语言与结构，理解商业逻辑与规则',
          logicChain: [
            '基础解码：如何攻克商务文书中的语言难关？-> (C6) 掌握商务日语核心语法与词汇（敬语、自谦语、固定搭配）。',
            '结构把握：如何快速抓住不同文书的写作目的和逻辑脉络？-> (C7) 解析商务文书的标准结构（邮件、报告、规章的起承转合）。',
            '内涵应用：如何理解文字背后的商业逻辑与社会常识并应用于解题？-> (C8) 应用商业惯例与规则（合同条款、公司规定、商业礼仪）。',
          ],
        },
        subsections: [
          {
            id: 'C6',
            title: 'C6. 商务日语核心语法与词汇——敬语、自谦语、固定搭配',
            body: '商务文书的理解首先建立在精准的语言基础上。核心是敬语体系：①尊敬语（Sonkeigo）——抬高对方的表述，如「お/ご～になる」「～られる」；②自谦语（Kenjōgo）——通过贬低自己来间接抬高对方，如「お/ご～する/いたす」「申し上げる」「拝～」；③郑重语（Teineigo）——使用「です・ます」等结尾。固定搭配：「～に限る」（最好莫过于）、「～に遅れる」（迟到）、「～一方だ」（持续/越来越）、「～にわたって」（历经/涉及）。惯用语：「目にかかる」（映入眼帘）、「見当がつかない」（心里没数）。',
            highlights: '⚠辨析：「お/ご～になる」（尊敬语，对方做）vs「お/ご～する」（自谦语，己方做）；「拝見する」（自谦，我看）vs「ご覧になる」（尊敬，您看）。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '电话场景敬语实战',
              points: [
                {
                  number: 1,
                  title: '自谦语在电话场景的应用',
                  content: '被问"田中部长在吗？"，如果田中不在，用「外出しております」（自谦体）而非「外出しています」；"我会送去"用「お届けする」（自谦体「届ける」）；"代为询问"用「お聞きしましょう」（自谦体「聞く」+提议「ましょう」）。',
                },
                {
                  number: 2,
                  title: '「拝見」使用场景',
                  content: '「拝見する」是自谦语，表示"（我）拜读/看"。收到对方的贵重资料时，用自谦语表示己方的动作。例题：Q9中，是"我"收到资料，所以用「拝見」而非「ご覧になる」（那是对方看的尊敬语）。注意「～中」读法：「営業中（えいぎょうちゅう）」「検討中（けんとうちゅう）」需结合词汇记忆。',
                },
              ],
            },
          },
          {
            id: 'C7',
            title: 'C7. 商务文书结构解析——邮件、报告、规章的起承转合',
            body: '理解文书整体框架能快速定位核心信息（用件）。商务文书基本结构：前文（「拝啓」+时令问候+感谢/道歉）→ 主文（「さて」「つきましては」「なお」等连接词引出核心事项，这是阅读和解题的最关键部分）→ 末文（总结、请求、「敬具」等）→ 记（以「記」开头，分条列举具体信息）→ 附记（「以上」或补充说明）。识别不同文书类型：①公司搬迁通知——主文由「さて」引出，核心是"事業所名称与地址变更"；②样品交货邮件——核心是告知发货日期并附上规格书；③预约说明会邮件——主文直接说明事由，提供可选时间；④高额支出申请书——快速定位"目的"栏。',
            highlights: '快速阅读策略：看到「拝啓」…「敬具」的格式，跳过礼仪性套话，迅速用眼睛扫描「さて」「つきましては」「なお」这些"信号词"，后面跟着的往往是核心内容。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '图表结合文书的阅读方法',
              points: [
                {
                  number: 1,
                  title: '图表结合题（问题6、12）',
                  content: '先读题干和图表标题，明确比较维度（如销售额、利润），再带着问题看图。问题12中，题干描述是"销售额去年激增""利润稳步增长但未达首位"，结合图表，只有C社符合"销售额去年陡增"且"利润持续为正但非最高"的特征。',
                },
                {
                  number: 2,
                  title: '报告书的逻辑结构',
                  content: '事故报告书：题目问"事故背景"，需定位到"2. 事故原因"下的"事故発生背景"段落。高额支出批准申请书：快速定位"目的"栏，文中明确写道"作为今年度重点经营策略的一环……构建与MJ商事之间的在线数据交换系统"。记住「さて」和「ただし」后面多写有主要事项。',
                },
              ],
            },
          },
          {
            id: 'C8',
            title: 'C8. 商业惯例与规则应用——合同条款、公司规定、商业礼仪',
            body: '最高阶的能力，要求理解文本中隐含的商业、法律常识和社会规则。①合同条款逻辑：例如"期满30日前，若任何一方未以口头或书面提出修改，则自动续约1年"——题目设定日期是3月1日（临界点），如果双方均无动作，结果是"无需手续，自动续约"；②公司理念：谈话先以数据对比引出，然后明确点出基本方针「"お客様の信用"を基本方針とし…」；③财务报告因果逻辑：销售额达标→费用方面有削减因素，但原油价格上涨导致原料费大幅增加→结果利润减少；④图表综合判断：必须将定性描述（如"积极海外展开奏效"）与图表定量数据（销售额曲线、利润柱状图）相匹配。',
            highlights: '⚠细节与主旨的区分：社长谈话会提到多个方面（环境、地域交流），但问题问"公司最重视的是什么"，必须找到统领全文的基本方针，而非具体举措。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '合同条款陷阱与图表注意事项',
              points: [
                {
                  number: 1,
                  title: '合同续约陷阱（问题1）',
                  content: '很多考生会纠结于"需要口头还是书面提出"，但题目设定的日期（3月1日）是"自动续约"生效的临界点。在这一天，如果双方均无动作，根据条款，结果就是"无需手续，自动续约"。考查的是对条款结果的理解，而非动作的形式。',
                },
                {
                  number: 2,
                  title: '损益平衡点（Break-even Point）图表',
                  content: '问题6关于"固定費"在损益平衡点分析中的位置，需要理解成本结构基本模型：总成本=固定成本+变动成本。销售额线穿过总成本线的点即损益平衡点。务必注意图表坐标轴的单位、图例以及趋势，避免张冠李戴。',
                },
              ],
            },
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════
  // 第2部  聴読解
  // ════════════════════════════════════════════════════
  8: {
    chapterTitle: '第2部聴読解',
    coreQuestion: '在真实的商务环境中，如何从混杂着图表、文字和对话的复杂信息流中，快速、准确地定位关键信息，并做出正确的判断或选择？',
    sections: [
      {
        id: 'B1',
        title: 'B1. 基础信息处理：图表解读与条件筛选',
        intro: {
          overview: '章前总览：一切分析的基础都始于对单一、明确信息的精准把握。本章是信息处理能力大厦的"地基"，核心任务是解答：如何从一份图表或表格中，快速、零失误地找到题目所要求的那个"唯一"答案？',
          highlight: '快速零失误地找到唯一答案',
          logicChain: [
            'C1. 问题焦点——明确题目问的是什么具体指标或结果。',
            'C2. 图表要素——理解图表坐标、数据单位、分类项目。',
            'C3. 筛选与计算逻辑——根据条件进行比对或计算，得出答案。',
          ],
        },
        subsections: [
          {
            id: 'C1',
            title: 'C1. 问题焦点——明确题目问的是什么具体指标',
            body: '这是解题的第一步，也是最关键的一步。必须像雷达一样，瞬间锁定问题的核心指令。问题焦点的常见类型：①筛选型——找出"符合某个条件"的项，如"全社平均1.5倍以上的部门是哪个"；②极值型——找出"最大/最小""最早/最晚"的项，如"比率增长最大的商品是？"；③计算型——需要简单四则运算，如"月复印1万张以上时，哪家公司最有利？"（需要计算总费用）；④判断型——基于图表信息判断某个陈述，如"下午1点时，公司里有谁？"。',
            highlights: '⚠陷阱：忽略或误解限定条件。题目中每一个字都是有用的。例如"月1万枚以上"这个条件会完全改变四家公司的费用对比结果；"午前中已预约第2会议室，午後10人使用"——时间和人数都是必须同时满足的筛选条件。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '问题焦点类型实战',
              points: [
                {
                  number: 1,
                  title: '筛选型 vs 计算型的区别',
                  content: '筛选型直接从图表查找，如：找"比率超过1.5倍"的部门，直接看柱状图超过150%的部分即可。计算型需要代入计算，如复印机费用题：总成本=固定费+使用费×张数，需要对每家公司计算N≥10,000时的总成本，通过比较使用费率（低的在大量使用时优势更大），判断哪家最有利。',
                },
                {
                  number: 2,
                  title: '判断型题目的典型陷阱',
                  content: '日程表判断题"下午1点时，公司里有谁？"——需要逐个核对每位人员在13:00的状态（在外/在公司/在会议中）。不能只看事件，还要关注时间段的起止。例如"14:00访问UPR証券"——14:00才出发，13:00该人应仍在公司。',
                },
              ],
            },
          },
          {
            id: 'C2',
            title: 'C2. 图表要素——快速建立与视觉材料的连接',
            body: '在明确问题后，需要快速与信息载体（图表）建立连接，知道去哪里找数据。图表要素是提取数据的"地图"和"刻度尺"：①标题——告诉你它展示的是什么（如"部门别加班时间与平均对比"）；②坐标轴——必须立刻看清横轴（X-axis，通常为时间、项目）和纵轴（Y-axis，通常为数量、金额、百分比）分别代表什么；③图例（Legend）——当图表中有多种数据系列时（不同颜色柱子），图例是区分钥匙；④数据标签——直接标注在图形上的具体数值，可免去读坐标步骤；⑤表格的行与列——要看清表头，明确每行和每列的分类标准；⑥单位（%、円、kg等）——看错单位是致命错误。',
            highlights: '为什么要快速识别图表类型？柱状图擅长比较不同类别数值大小；折线图擅长展示随时间变化的趋势；饼图擅长展示整体中各部分的构成比；表格用于呈现结构化详细数据，适合多条件查找。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '深度应用：跨图表数据关联',
              points: [
                {
                  number: 1,
                  title: '同时出现两个饼图的情况',
                  content: '练习第7题中，同时出现了"销售额"和"消费量"两个饼图，题目问的是"销售额对消费量的效率"，这提示需要跨图表关联数据，而非只看单个图。操作：将两个饼图中同一部门的占比进行比较（如A所：売上29% vs 消費量22%），计算比值（29÷22≈1.32）作为效率指标，找出比值最高的部门。',
                },
                {
                  number: 2,
                  title: '条形图中"超过平均"的读法',
                  content: '"部門別残業時間"条形图：图中数值是相对于全社平均的百分比。超过100%意味着该部门加班时间高于全社平均。常见错误：将100%误读为100小时或某个具体时长。切记：这里的数值是相对指标，不是绝对小时数。',
                },
              ],
            },
          },
          {
            id: 'C3',
            title: 'C3. 筛选与计算逻辑——精确应用规则得出答案',
            body: '执行阶段，需要像机器一样精确无误地应用规则。标准操作流程（4步）：①定位数据——根据问题焦点，在图表中找到相关的一个或多个数据点；②应用条件——将找到的数据与题目条件逐一比对（比对：如">1.5倍平均"；排序：如找出"增长最大"，需要计算差值后比较）；③执行计算——加减乘除：如总费用=单价×数量+月租；计算增长率=(今年-去年)/去年；比较比率：用（销售额占比÷消费量占比）得到效率比值进行比较；④得出答案——将唯一满足所有条件的结果对应到选项。',
            highlights: '应对"隐含计算"或"多步骤筛选"的关键：必须将题目文字描述与图表信息完整地、逻辑地整合在一起，不能有遗漏。例：条件1（隐含）上午已预约第2会议室，下午不能再用；条件2（明确）下午使用需容纳10人→综合两个条件才能找到正确答案。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '复印机费用计算（典型多条件计算题）',
              points: [
                {
                  number: 1,
                  title: '设置计算模型',
                  content: '总成本 = 固定費 + (使用費 × 使用枚数)。使用量为"1万枚以上"，设N≥10,000。A社：40,000+2.0N；B社：30,000+3.5N；C社：35,000+2.5N；D社：45,000+1.0N。',
                },
                {
                  number: 2,
                  title: '关键推断：大量使用时可变成本更重要',
                  content: '比较D社（使用费1.0日元）与其他社：令D社成本≤C社成本，即45,000+1.0N≤35,000+2.5N，解得N≥6,667。由于题目条件N≥10,000，因此D社最有利。解题核心是识别出在大量使用场景下，可变成本（每张费用）比固定成本更重要。',
                },
              ],
            },
          },
        ],
      },
      {
        id: 'B2',
        title: 'B2. 对话逻辑分析：意图推断与条件整合',
        intro: {
          overview: '章前总览：在掌握了从单一图表中提取信息的基础能力（B1）之后，本章将面对更贴近真实商务场景的挑战：理解一段完整的对话。核心问题是：如何从一段包含寒暄、转折、提议和确认的商务对话中，剥离出核心议题，并整合多个条件，最终推断出说话人的真实意图或事实结果？',
          highlight: '像侦探一样从对话"碎片"中拼凑出真实意图和最终结论',
          logicChain: [
            '对话在讨论什么？-> 识别核心话题与场景 (C4)',
            '对话中提出了哪些具体要求或限制？-> 捕捉关键条件与变更 (C5)',
            '这些条件如何相互作用，导向最终结论？-> 进行意图与结果推理 (C6)',
          ],
        },
        subsections: [
          {
            id: 'C4',
            title: 'C4. 对话话题与场景——快速定位对话的"舞台"',
            body: '这是分析对话的第一步，目的是快速定位对话发生的"舞台"和"主题"。话题（Topic）是对话双方围绕的核心议题，如：预约餐厅、讨论调查问卷项目、决定培训主题。场景（Scenario）是对话发生的具体商务情境，如：预约安排、会议讨论（生产说明会、经营会议）、电话沟通、内部商讨、客户调查。识别话题的三种方法：①关注开头与结尾——对话开头交代背景（如"关于新员工培训…"），结尾常点明最终决定；②抓住高频关键词——反复出现的名词或动词往往是话题核心（如反复出现"研修"，话题就是培训安排）；③利用问题提示——题目本身通常明确提问方向（如"女の人は何を予約しますか"直接锁定话题是"预约行为"）。',
            highlights: '⚠注意区分话题（宏观的"讨论什么事"）和细节（具体的"怎么做、什么要求"）。例如，话题是"预约餐厅"，细节是"时间、人数、菜品偏好"。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '预约餐厅话题的条件追踪',
              points: [
                {
                  number: 1,
                  title: '话题识别的重要性',
                  content: '在确定了话题（如"预约餐厅"）之后，大脑会自动预判接下来需要捕捉的信息类型：时间、地点、人数、菜品要求、价格范围等。如果一开始没有识别出话题，听到这些细节时就无法将它们关联起来，最终对整个对话的理解会支离破碎。',
                },
                {
                  number: 2,
                  title: '高频场景识别',
                  content: '商务聴読解常见话题：①培训/研修安排（研修・セミナー）；②会议室/餐厅/场地预约；③人员出差/出行安排；④数据分析与汇报讨论；⑤产品/服务方案比较选择。对话刚开始时就要快速定位属于哪个类型，以便提前准备捕捉相关条件。',
                },
              ],
            },
          },
          {
            id: 'C5',
            title: 'C5. 关键条件与变更——收集影响最终决定的所有线索',
            body: '这是分析对话的第二步，目的是像侦探一样收集所有影响最终决定的"线索"。条件（Condition）是对话中明确提出的、必须满足的要求或限制，如："要5个人的位子""必须是日料""费用要控制"。变更（Change）是对话过程中新出现的、可能补充/修改/否定之前条件的信息，如：最初想订A餐厅，但发现A餐厅已满，转而讨论B餐厅。捕捉条件的方法：①记录明确表述——特别注意「～たい・ほしい（想要）」「～なければならない（必须）」「～はだめ（不行）」「～がいい（比较好）」等句型；②关注转折词——「でも・しかし・ただ」后常跟重要条件变更；③注意数字和专有名词——时间、人数、金额、商品名、地名等是核心条件，必须准确记录。',
            highlights: '⚠为什么"变更"如此重要？商务对话是动态的。最初方案（条件A）可能因新信息（条件B）而变得不可行。解题关键在于追踪条件的演变路径，单纯只看条件A或B都会选错。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '条件演变路径示例',
              points: [
                {
                  number: 1,
                  title: '初始条件+变更条件的整合',
                  content: '初始条件：想订一家受欢迎的餐厅。变更/新条件：但客人中有外国客户，可能想吃日料。推理整合：最终选择需要同时满足"受欢迎"和"提供日料"两个条件。单纯只看条件A（受欢迎）或B（日料）都会选错。正确答案必须同时满足两个条件。',
                },
                {
                  number: 2,
                  title: '条件中的隐含限制',
                  content: '有些条件是隐含的，并未直接说出。例如"我们公司只能用A级以上的供应商"——这是一个隐含的筛选条件，但对话中可能通过提到"A社的等级是B级，这…"来暗示。听力时要注意这种间接否定的表达方式。',
                },
              ],
            },
          },
          {
            id: 'C6',
            title: 'C6. 意图与结果推理——将条件整合为最终结论',
            body: '这是分析对话的第三步，需要将C4和C5收集到的信息进行逻辑整合。常见逻辑关系类型：①并列关系——必须同时满足多个条件，常用「そして・また」连接；②转折关系——后一条件部分否定/限制前一条件，常用「しかし・でも」连接（高频陷阱区）；③因果关系——因为某个条件，所以产生了某个结果，常用「だから・したがって」连接。推理操作步骤：①建立条件清单（列出对话中所有条件）；②分析条件优先级（转折词后的、被强调的通常是更高优先级）；③应用排除法（不符合任何一个硬性条件的选项即可排除）；④寻找唯一解（能最大程度满足所有条件的选项）。',
            highlights: '⚠警惕"片面理解"：不要只抓住对话中某一个条件（尤其是开头提到的）就匆忙下结论。必须听完整个对话，整合所有条件，特别注意最后的转折和决定。很多错误选项就是利用对话前半部分提到的、但后来被修改或放弃的条件来迷惑考生。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '例题精讲：日程表修改题',
              points: [
                {
                  number: 1,
                  title: 'C4+C5整合分析',
                  content: '话题：通过电话修改日程表。条件1（初始）：男同事说昨晚日本商事的泽田科长希望今早去一趟。条件2（现有日程）：11点营业会议，1点企划会议，3点访问东京产业。条件3（新要求）：在日程表9点追加"访问日本商事"。条件4（冲突与解决）：如果日本商事那边一结束就赶过去，如果可能来不及开会，请给会议录音。',
                },
                {
                  number: 2,
                  title: 'C6推理与结论',
                  content: '最终日程：9点访问日本商事（新增硬性条件）、11点营业会议（原有项目，但出席方式可能变更）、1点企划会议（原有）、3点访问东京产业（原有）。对于11点会议，处理方式是"可能出席，如果来不及就录音"，该会议依然在日程表上。因此正确选项必须同时反映新增9点事项并保留11点会议项。其他选项或漏掉新增事项，或直接删除会议项，均不符合。',
                },
              ],
            },
          },
        ],
      },
      {
        id: 'B3',
        title: 'B3. 综合决策分析：多源信息整合与策略理解',
        intro: {
          overview: '章前总览：在掌握了单一图表解读（B1）和单一对话逻辑分析（B2）之后，本章将面对最复杂的真实商业场景模拟。核心问题是：当决策者需要同时处理视觉资料（如复杂图表）和听觉信息（如高层讨论）时，如何通过信息交叉验证与综合判断，理解并推断出最终的商业决策或战略方向？',
          highlight: '在多维度信息交叉中，把握决策者的核心战略意图',
          logicChain: [
            'C7：图文信息对应——如何将听到的讨论要点与图表中的具体数据精准对应？',
            'C8：多维度权衡分析——如何在多个（甚至相互矛盾的）评价维度之间进行综合比较？',
            'C9：战略侧重点判断——如何透过讨论的措辞和语境，准确把握决策者的核心战略意图？',
          ],
        },
        subsections: [
          {
            id: 'C7',
            title: 'C7. 图文信息对应——将抽象标准与具体数据挂钩',
            body: '第一步也是最重要的一步，是建立听觉信息与视觉材料之间的精确映射关系。信息映射（Information Mapping）是指将对话中提到的定性描述（如"最重视政治安定""成本效益比突出""销售情况良好"）与图表中的定量或分类数据（如"政情"评分、"C/P值"、"销售额增长率"）一一对应起来的过程。关键动作：边听边看，在图表上快速定位对话中提到的关键词。',
            highlights: '⚠映射错误：最常见错误是"张冠李戴"——对话在讨论A计划，眼睛却看着图表中的B计划数据；⚠术语不对应："收益性"在对话中可能被说成"profitability""回报率"或"C/P值"，需根据上下文灵活理解建立等价关系。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '图文对应三步骤示例',
              points: [
                {
                  number: 1,
                  title: '三步骤：定位项目→定位指标→确认数据',
                  content: '听到对话："关于法人推进计划……从成本效益比（C/P值）的观点来看，特别值得作为主力。"步骤一（定位项目）：对话提到"法人推进计划"，对应图表中"法人推進"这一行。步骤二（定位指标）：对话提到"C/P值"，判断图表中哪个指标栏（"収益率"或"C/P"）代表这一概念。步骤三（确认数据）：查看"法人推進"行与"収益性"列交叉的单元格，验证是否显著优于其他计划。',
                },
                {
                  number: 2,
                  title: '问卷项目信息匹配题',
                  content: '材料P37的"问卷项目"题：听力会描述不同提案者的偏好（如"○○さんは、値段が安いことを重視しています"）。需要将"安い"与选项中的"廉価"对应，在○○的提案中找到包含"廉価"的选项组合，同时确保其他选项组合不含此关键词或包含了错误关键词。',
                },
              ],
            },
          },
          {
            id: 'C8',
            title: 'C8. 多维度权衡分析——超越单一指标的全局观',
            body: '真实的商业决策很少只基于一个完美指标。决策者需要在增长性（未来潜力）、收益性（当前利润）、风险性（不确定性）、成本、实施难度等多个维度间进行权衡（多准则决策分析，MCDA）。典型权衡模式：①高增长 vs 高风险——新兴市场拓展增长率高但政治/汇率风险大；决策关键：公司当前的风险承受能力和战略阶段（开拓期vs稳定期）。②高收益 vs 高投入——研发新产品长期收益高但需要巨额前期投入；决策关键：公司资金充裕度和对短期业绩的要求。',
            highlights: '案例应用：高层讨论某地产项目时，部长指出"租赁公寓空置率高""酒店客流量减少"（负面维度），社长则说"市中心公寓销售情况良好""希望职住接近的人增多"（正面维度）。不能因部长提到A/B方案风险就全盘否定，也不能因社长提到C方案利好就盲目选择——需要在规避高风险的同时顺应积极的市场趋势。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '案例分析：地产项目方案比较',
              points: [
                {
                  number: 1,
                  title: '图表维度与对话维度的整合',
                  content: '图表展示了四家建筑公司的提案（租赁公寓、酒店、商铺、出售型公寓），附有预期收益率、空置率风险、投资额、市场趋势等指标。对话提供了定性判断：部长指出租赁和酒店的风险，社长指出出售型公寓的市场机会。最终判断：在规避高风险（租赁、酒店）的同时，顺应积极市场趋势（出售型公寓畅销），选择"出售型公寓与个人住宅组合"方案。',
                },
                {
                  number: 2,
                  title: '权衡分析的思维框架',
                  content: '①先列出所有维度（增长性、收益性、风险、成本）；②标注每个方案在每个维度上的表现（+、-、中立）；③看决策者（尤其是社长）在对话中最关注的是哪个维度；④找出在该最优先维度上表现最好，同时在其他维度上没有明显致命弱点的方案，即为答案。',
                },
              ],
            },
          },
          {
            id: 'C9',
            title: 'C9. 战略侧重点判断——解读决策者的"潜台词"',
            body: '这是解题的"最后一公里"，需要从对话的语气、措辞、关注点的优先级中，判断出决策者当下的核心战略意图。战略意图类型：①强化/主力化——「一番強化する・主力として位置付ける・特に注力する」→集中资源重点突破；②维持/稳定——「従来通りでいい・現状維持・順調に進んでいる」→不求激进，巩固现有成果；③准备/孵化——「準備段階・次年度本格化・調査中」→暂时非重点，为未来布局；④担忧/规避——「～が課題だ・～が止まっている・～に苦戦している」→风险预警，可能倾向规避。',
            highlights: '发言者身份与语境：社长的发言侧重宏观战略/市场趋势/长期方向；部长的发言侧重具体运营/数据/执行风险。最终决策通常体现社长战略方向与部长风险提示的平衡。"经营计划说明会"基调是设定未来方向，重点找"强化点"；"具体项目讨论会"基调是做出具体选择，重点在"方案比较"。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '从语言细节捕捉战略意图',
              points: [
                {
                  number: 1,
                  title: '关键词信号：强化 vs 维持 vs 准备',
                  content: '强化：「一番強化する」「主力として位置付ける」「特に注力する」→如第13题，将"成本效益比突出"的法人推进计划作为强化重点。维持：「従来通りでいい」「現状維持」「順調に進んでいる」→不求激进，巩固现有成果。准备：「準備段階」「次年度本格化」「調査中」→暂时不是重点，为未来布局→如第13题的网络限定计划。',
                },
                {
                  number: 2,
                  title: '最终决策的逻辑',
                  content: '综合C7（信息对齐）和C8（多维度权衡）的分析后，最终选择必须最贴合对话中流露出的最高优先级战略意图。如果整个对话基调是"控制风险、求稳定"，那么即便某个方案增长潜力大但风险高，也不会被选中。即使多个方案都有正面指标，也要判断哪个方案最符合当前战略基调（进攻/防守/转型）。',
                },
              ],
            },
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════
  // 第3部  読解
  // ════════════════════════════════════════════════════
  9: {
    chapterTitle: '第3部読解',
    coreQuestion: '如何系统性地检验和提升在真实、复杂的日本商务环境中，运用日语进行有效沟通与信息处理的综合能力？',
    sections: [
      {
        id: 'B1',
        title: 'B1. 听力理解：在纯听觉环境中捕捉商务信息',
        intro: {
          overview: '章前总览：在没有任何文字、图表等视觉辅助的"纯听觉"商务场景中，我们如何才能做到不遗漏、不误解关键信息？检验学习者在无视觉辅助的情况下，能否准确理解商务对话、会议、电话等内容，并抓住核心事实、意图和逻辑关系。',
          highlight: '在纯听觉环境下捕捉商务信息的核心能力',
          logicChain: [
            '如何听懂"商务框架"？-> 掌握商务场景的固定套路与表达（电话、会议等）(C1)',
            '如何听懂"商务内容"？-> 熟悉商务领域的高频词汇与概念（业种、职位、流程等）(C2)',
            '如何听懂"言外之意"？-> 辨析口语中的省略、委婉与真实意图推断 (C3)',
          ],
        },
        subsections: [
          {
            id: 'C1',
            title: 'C1. 商务场景定型表现——电话、会议、交涉等固定用语',
            body: '商务日语听力并非漫无边际的日常对话，而是发生在高度结构化、目的性强的场景中。掌握这些场景的"固定剧本"能极大降低听力负担，实现预测性聆听。场景一（电话应对）：开头「○○株式会社でございます」「いつもお世話になっております」；转接/找人「少々お待ちください。ただいま、担当の者と代わります」「申し訳ございません。ただいま席を外しております」；留言/结束「伝言をお願いできますか？」「それでは、失礼いたします」。场景二（会议）：开场「本日の議題は…についてです。それでは、始めさせていただきます」；发表意见「一点、質問させていただいてもよろしいでしょうか？」「ご意見に賛成です。」「おっしゃることはよくわかりますが、別の角度から考えますと…」；总结「本日の結論を確認させていただきます。では、次回の会議日程ですが…」。',
            highlights: '注意区分会议主持者（多用引导性/总结性/流程控制性表达「～させていただきます」「～についてご意見をお願いします」）和参会者（多用提出/赞同/反对/确认等表达）的语言特征。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '为什么电话场景固定用语如此重要？',
              points: [
                {
                  number: 1,
                  title: '信息密度高且无重复',
                  content: '电话沟通通常直奔主题，关键信息（如人名、公司名、时间、数字）往往只说一遍，没有文字确认的机会。流程化降低认知负荷：当你熟悉了"自报家门→确认对方→说明事由→处理/转接→结束寒暄"这个流程，大脑就能将更多精力集中在本次通话独有的、变动的信息上，而不是被基础流程用语分散注意力。',
                },
                {
                  number: 2,
                  title: '通过语言特征判断发言者身份',
                  content: '使用正确的定型表现，本身就是商务素养的体现，也是听力考试中判断说话人身份和关系的重要线索。例如：使用大量「～させていただきます」的人通常是主持者或级别较低者；使用「おっしゃる通りですが」的通常是有礼貌的反对者；使用「以上で、本日の会議を終了いたします」的是会议主持者。',
                },
              ],
            },
          },
          {
            id: 'C2',
            title: 'C2. 商务基本单语——业种、职位、交易流程等关键词',
            body: '商务基本单语（基本単語）是在商务活动中频繁出现的、具有特定商业含义的词汇和概念，是理解具体业务内容的基础。①组织与职位：业种（製造業・小売業・IT業界・金融機関）；部门（営業部・経理部・人事部・開発部）；职位（社長・部長・課長・係長→注意层级关系；担当者・役員）；②业务流程与文书：流程（見積もり・発注・納品・請求・決済・在庫）；文书（契約書・請求書・見積書・報告書・議事録）；③数据与趋势：增加（増加する・増える）；减少（減少する・落ち込む）；比较（～に比べて・～を上回る/下回る・～割）。',
            highlights: '高效记忆法：①场景关联——学习"会议"词汇时连带记忆「議題・結論・賛成/反対」；②词根词缀——「～書（文书）」「～料/費/代（费用）」「～率（比率）」，掌握规律后，即使遇到生词也能根据上下文合理推测。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '场景关联记忆与词根词缀',
              points: [
                {
                  number: 1,
                  title: '词根规律表',
                  content: '「～書」多指文书（契約書・請求書・見積書・報告書・仕様書）；「～料」「～費」「～代」多指费用（交通費・人件費・光熱費・使用料）；「～率」多指比率（利益率・成長率・回収率・達成率）；「～先」多指对象方（取引先・仕入先・販売先・得意先）。',
                },
                {
                  number: 2,
                  title: '数据与图表相关听力词汇',
                  content: '趋势描述：増加する・減少する・横ばい（持平）・ピーク（峰值）・底を打つ（触底反弹）。比较描述：～に比べて（与～相比）・～を上回る/下回る（超过/低于）・～割（比例）。预测描述：見込む（预计）・予測される（预测为）・目標を達成する（达成目标）。虽然B1是"纯听觉"，但听力材料中常会口头描述数据趋势，为B2（听读解）做铺垫。',
                },
              ],
            },
          },
          {
            id: 'C3',
            title: 'C3. 口语中的省略、委婉与真实意图推断',
            body: '这是商务日语听力的最高阶挑战，也是区分"听懂字面意思"和"听懂真实意图"的关键。日语，尤其是在商务场合，充满了"腹芸"（心照不宣）和"建前"（场面话）。省略：主语省略（"来週の会議の資料を準備します"——必须根据场景判断是谁在准备）；借助上下文省略。委婉表达：①否定疑问表建议「～ませんか？」"もう一度検討しませんか？"（真实意图：我认为应该重新考虑）；②模糊语「ちょっと難しいです」（真实意图：几乎等于拒绝）；③「検討させていただきます」（真实意图：在商务中常是委婉拒绝的代名词）；④"但是（が、しかし）"之后是重点——「お考えはよくわかりますが、予算の関係で…」（"但是"之后才是核心立场）。',
            highlights: '应对策略：永远不要只停留在字面。结合说话人身份（客户还是同事？）、语气（热情还是冷淡？）、前后逻辑（前面在抱怨什么？后面提出了什么方案？）进行综合推断。当听到非常礼貌、正面但内容空泛的表达时，要警惕其背后可能隐藏的负面意图。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '字面意思 vs. 社交含义对照表',
              points: [
                {
                  number: 1,
                  title: '典型委婉表达的真实含义',
                  content: '「検討させていただきます」→实际意思：拒绝或搁置，不会真的去"研究"；「おっしゃる通りですが…」→"但是"之后才是真实立场；「ちょっと難しいかもしれません」→几乎等于拒绝；「前向きに検討します」→可能是礼貌拒绝，也可能真的会考虑（需结合语气）；「少し時間をいただけますか」→可能是推迟决定，也可能是拒绝的前奏。',
                },
                {
                  number: 2,
                  title: '主语省略的推断技巧',
                  content: '日语对话中，主语经常省略，必须根据场景判断是谁在做动作。例如：A说「今日、田中さんいらっしゃいますか」，B说「ただいま外出しております」——省略的主语是田中さん（不是B自己外出）。关键：动作的主语由对话的参与者身份关系和已有上下文决定，不能望文生义。',
                },
              ],
            },
          },
        ],
      },
      {
        id: 'B2',
        title: 'B2. 听读解：整合听觉与视觉信息的综合判断',
        intro: {
          overview: '章前总览：当信息同时通过"听"（对话、说明）和"看"（图表、文书）两种渠道涌来时，我们如何高效整合、交叉验证，并最终做出正确的判断或决策？检验学习者能否同步处理听觉和视觉信息，进行综合分析与逻辑推理，解决实际问题。',
          highlight: '同步处理视听信息，执行"问题解决循环"',
          logicChain: [
            '第一步：识别与定位-> (C4) 如何快速、准确地解读各类商务视觉材料（图表、单据、日程表）？',
            '第二步：关联与推理-> (C5) 如何在听觉信息流中，定位与视觉材料相关的关键点，并建立两者间的逻辑桥梁？',
          ],
        },
        subsections: [
          {
            id: 'C4',
            title: 'C4. 商务视觉材料的系统解读——图表、单据、日程表',
            body: '视觉材料（Visual Materials）是解题的"地图"和"证据库"。图表（Graphs/Charts）解读要点：先看标题与坐标轴（明确图表主题及横/纵轴含义）；抓趋势与比较（线条的上升/下降/持平，以及不同柱状或扇形之间的大小关系）；锁定极值与拐点（最高点、最低点、显著变化的时点往往是设问关键）。单据与表格（Forms/Tables）解读要点：明确项目与对应值（快速扫描行/列标题，形成"项目-值"对应关系）；关注特殊标记与空白（"★""※"等注释符号，以及空白单元格）；理解流程与关联（申请流程表本身体现步骤顺序）。日程表与计划表（Schedules/Plans）解读要点：掌握时间框架（日/周/月计划，时间单位）；识别冲突与空档（时间重叠处与未被安排的时段是调整类题目的核心）；注意参与人与地点（除时间外，谁在哪里也是关键信息）。',
            highlights: '⚠"推移"vs"構成比"：推移图（折线/柱状图）看时间序列上的变化；構成比图（饼图/堆叠柱状图）看某一时间点上各部分的占比。混淆两者会导致对数据关系的根本性误读。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '"预判-定位"两阶段解读法',
              points: [
                {
                  number: 1,
                  title: '阶段1：预判（听前）',
                  content: '播放听力前有数十秒时间阅读视觉材料。目标不是记住所有细节，而是建立信息框架和问题意识。例如，看到一张有四个季度的销售额柱状图，应立刻预判：听力中可能会讨论"哪个季度最高/最低""为什么某个季度增长/下降""明年预测如何"等问题。',
                },
                {
                  number: 2,
                  title: '阶段2：定位（听中）',
                  content: '听力开始后，将听到的关键词（如"第3四半期""予算""変更"）迅速与视觉材料中的对应位置（横轴Q3、预算栏、某个计划项）进行匹配。这就像一边听导航指令，一边在地图上找到当前位置和目标位置。',
                },
              ],
            },
          },
          {
            id: 'C5',
            title: 'C5. 听觉与视觉信息的关联与推理逻辑',
            body: '这是"听读解"能力的灵魂，即搭建"听"与"看"之间的逻辑桥梁。信息关联的三种模式：①对应确认型——听力内容直接指向视觉材料中的具体项目或数据，要求确认其数值或状态（听力：「去年の売上で一番高かったのはどの商品でしたっけ？」→对应图表查找）；②因果解释型——视觉材料展示现象，听力内容解释原因（或反之），需要建立"现象-原因""目标-手段"的因果链；③条件变更型——最高频且最具挑战，听力提出新条件，要求在视觉材料基础上推导新结果（如日程表发生变化后，判断对其他任务的影响）。',
            highlights: '核心推理技巧：排除法——用视觉信息直接排除明显错误选项（数据不符、时间冲突）；交叉验证——用听力细节去验证视觉材料的解读，或用视觉事实去检验听力陈述，两者必须逻辑自洽。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '"问题解决循环"思维模型',
              points: [
                {
                  number: 1,
                  title: '五步问题解决循环',
                  content: '①识别问题：通过听力，明确说话人面临的核心问题或目标（如"哪个时间段会议室都空着？"）；②提取条件：从听力中提取所有限制条件（时间、预算、人员、客户要求等）；③检索地图：带着"问题"和"条件"，到视觉材料这张"地图"中检索相关信息；④执行推理：在"地图"上应用"条件"进行推演（如：将新会议时间插入日程表看是否冲突）；⑤得出结论：推演出的可行解即为答案。',
                },
                {
                  number: 2,
                  title: '条件变更型的典型场景',
                  content: '视觉材料：项目日程表。听力："客户原因，设计确认会议从周四提前到周三。随之，资料准备的期限也……"→需要在脑中动态调整日程表，并判断对其他任务的影响。这类题目的关键是：先确认变更了什么（周四→周三），再追问"这个变更会对哪些任务产生连锁影响"（最近的下游任务：资料准备）。',
                },
              ],
            },
          },
        ],
      },
      {
        id: 'B3',
        title: 'B3. 阅读理解：快速处理各类商务文书并做出决策',
        intro: {
          overview: '章前总览：在掌握了从纯听觉（B1）和视听结合（B2）中提取信息的能力后，本章将聚焦于商务场景中最核心、最密集的信息载体——商务文书。核心任务是：面对一份正式的商务文书，如何快速、准确地抓住其核心意图、关键要件，并做出正确的实务判断？检验学习者对商务文书（如契约书、商务信函、报告、图表等）的快速阅读、深度理解与信息提取能力，以及基于文本的实务判断力。',
          highlight: '快速阅读定位主文要件，理解深层商务逻辑',
          logicChain: [
            '识别文书类型与结构：这是什么类型的文书？它的标准格式是怎样的？(C6)',
            '定位核心信息：在这份文书中，哪里是必须关注的"主文"？如何快速找到关键条件和限制？(C7)',
            '理解深层含义：文书中使用了哪些特定的商务表达和概念？背后反映了怎样的商务常识与文化？(C8、C9)',
          ],
        },
        subsections: [
          {
            id: 'C6',
            title: 'C6. 商务文书类型与结构——社内/社外文书、契约书格式特点',
            body: '商务文书根据沟通对象分为两大类。社内文书（Internal Documents）：在公司内部流通，用于报告、申请、决策、记录等；格式相对灵活但需清晰准确高效；常用"報告書""企画書""決裁書/稟議書""議事録"等。社外文书（External Documents）：发送给公司外部（客户、合作伙伴、政府机构）的正式文书；格式严谨，用语极其礼貌，遵循固定礼仪；主要包括"ビジネスレター"（商务信函）和各类交易文件（契约书、订单、发票等）。契约书（Contract）：特殊的社间文书，具有法律约束力；条款严密，包含"有効期間""契約条件""解除条項""損害賠償"等关键部分。',
            highlights: '稟議書的特殊性：日本企业决策文化的典型体现——一项重要决定（如大额采购）需起草"稟議書"，在公司内部相关部门间传阅、征求意见并盖章（"決済を取る"），达成共识后提交最终决策者批准。体现了日本企业"根回し"（事前疏通）和集体决策的文化。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '格式与礼仪差异的根源',
              points: [
                {
                  number: 1,
                  title: '社外文书的格式规范',
                  content: '社外文书代表公司形象，必须使用最高级别的敬语和固定格式：「拝啓」开头→时令问候→感谢/道歉→「さて」主文→「つきましては」请求→总结→「敬具」结尾。社内文书更注重效率和信息的准确传递，敬语使用相对简化（通常只需「です・ます」体）。',
                },
                {
                  number: 2,
                  title: '契约书阅读重点',
                  content: '契约书阅读时重点关注：有効期間（有效期）、自動更新条項（自动续约条款——常见考点）、解除条項（解约条款，触发条件是什么）、損害賠償（违约责任，赔偿方式）、管轄裁判所（管辖法院，通常是卖方所在地）。特别注意条款中的"未満"（不满，不含临界值）vs"以上"（以上，含临界值）——这是高频考点陷阱。',
                },
              ],
            },
          },
          {
            id: 'C7',
            title: 'C7. 主文、要件与背景信息的提取——快速找到核心内容',
            body: '高效阅读的核心是快速定位主文和要件，不被冗长的前文和背景信息干扰。主文（Main Text）是文书中陈述核心事实、要求、决定、条件的部分，在商务信函中通常位于前文（问候语）之后。定位技巧：寻找标志性词语——「さて・つきましては・なお・ただし・ご注意ください」；关注段落开头——新的段落，尤其是第一个独立段落，往往是主文的开始。要件（Key Conditions）是主文中包含的具体条件、限制、例外或必须执行的事项。定位技巧：紧盯转折与条件词——「ただし（但是）・なお（另外）・…の場合（…的情况下）・…に限る（仅限于…）」后的内容几乎总是重要要件；识别列举与数字——分点叙述（①②…）的内容、具体日期/金额/百分比/数量，通常是需要提取的关键信息。',
            highlights: '快速阅读策略：战略性跳过高度格式化的礼貌用语（「拝啓」「時下ますますご清栄のこととお慶び申し上げます」），直接寻找「さて」等主文标志词。理解其存在是为了表达礼仪即可，核心信息不在此处。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '要件提取示例与应试技巧',
              points: [
                {
                  number: 1,
                  title: '合同自动续约条款提取',
                  content: '原文："有効期間が終了する30日前までに、契約変更の申出が口頭または書面でない場合、同一条件で自動的に1年延長される"。提取要件："30日前"（时间节点）、"口頭または書面でない場合"（触发续约的条件：双方无任何动作）、"自動的に1年延長"（结果：自动续约1年）。考点：在3月1日（距3月31日到期恰好30天），如果双方均无动作，结果就是自动续约，无需手续。',
                },
                {
                  number: 2,
                  title: '读懂连接词的信号功能',
                  content: '「さて」——主文开始信号，此后是核心事项；「つきましては」——进一步说明/提出请求；「なお」——补充说明，有时包含重要的附加条件；「ただし」——但书/例外，后面几乎都是重要限制条件；「以上」——文书正式结束；「記」——分条列举开始。掌握这些词的信号功能后，可以像"目录扫描"一样快速定位文书结构。',
                },
              ],
            },
          },
          {
            id: 'C8',
            title: 'C8. 商务惯用表现与概念——隐喻、行话与商业动态描述',
            body: '日语商务文书大量使用惯用句和隐喻表达，字面意思与实际含义相差甚远。常见商务惯用表现：「赤字体（Showing a deficit）」——字面"出现赤字"，即亏损，反义词「黒字体」（盈利）；「水を差す」——字面"泼冷水"，商务中意为（通过竞争等）拉开差距；「足がかりを築く」——建立立足点，常指进入新市场；「頭打ち」——达到顶点、增长停滞，描述业绩不再上升；「暗礁に乗り上げる」——谈判或计划遇到意外障碍，陷入僵局；「大台に乗る」——突破某个大关（如股价突破1000日元）；「うなぎ登り」——飞速上涨（像鳗鱼那样一路攀爬）；「底を打つ」——触底（达到最低点开始回升）。',
            highlights: '学习逻辑：不应死记硬背，而应理解其核心意象。与"水"相关→多与"差距""冷却"有关（水を差す・呼び水）；与"高/低"相关→多与"价格、业绩"变动有关（うなぎ登り・頭打ち・底打ち）；与"道路/阶段"相关→多与"过程、进展"有关（足がかり・正念場・大詰め）。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '结合例句记忆惯用表达',
              points: [
                {
                  number: 1,
                  title: '业绩与趋势描述',
                  content: '「売上高が頭打ちになっている」（销售额增长停滞）；「利益率がうなぎ登りに上昇している」（利润率飞速上升）；「業績が底を打ち、回復基調にある」（业绩触底回升，处于恢复轨道）；「新規事業が軌道に乗ってきた」（新业务步入正轨）；「計画が暗礁に乗り上げた」（计划遇到了障碍，陷入僵局）。',
                },
                {
                  number: 2,
                  title: '市场与战略描述',
                  content: '「アジア市場に足がかりを築く」（在亚洲市场建立立足点）；「ライバル社との差が水を差すように開いた」（与竞争对手的差距如泼水般拉开）；「大台の100億円を達成した」（实现了100亿日元的大关）；「正念場を迎えた交渉」（面临关键时刻的谈判）；「プロジェクトも大詰めだ」（项目也进入了最后冲刺阶段）。',
                },
              ],
            },
          },
          {
            id: 'C9',
            title: 'C9. 日本商务组织与交易常识——公司结构、交易流程、业绩指标',
            body: '理解文书内容离不开对日本商务基本常识的掌握。①公司组织与职务：典型日本企业组织是金字塔形"部-課-係"结构（社長→専務/常務→部長→課長→係長→社員）；在文书中准确识别发信人和收信人的职务，有助于理解文书的分量和背景。②重要称谓：称呼对方公司「御社（おんしゃ）/貴社（きしゃ）」（尊敬体）；称呼自己公司「弊社（へいしゃ）/小社（しょうしゃ）」（自谦体）；收到对方来函时用「拝読（はいどく）しました・拝受（はいじゅ）しました」。③交易流程（仕様書→見積書→注文書/契約書→納品書→請求書→領収書）：阅读技巧是先判断文书处于流程中的哪个环节，再有针对性地寻找该环节的核心信息（見積書找"价格"和"纳期"，請求書找"金额"和"支付期限"）。',
            highlights: '⚠关键辨析：①「～未満」（不满，不含临界值，如"30歳未満"=29岁及以下）vs「～以上」（以上，含临界值，如"30歳以上"=30岁及以上）——这是高频陷阱；②「引き継ぎ（工作交接）」vs「引けを取らない（不逊色于）」——同有"引"字但意思完全不同，需根据上下文区分。',
            aiSupplement: {
              label: 'AI 补充',
              intro: '交易流程与业绩指标',
              points: [
                {
                  number: 1,
                  title: '交易流程单据速记',
                  content: '仕様書（技术规格书）→ 見積書（报价单：找"价格"和"纳期"）→ 注文書/契約書（订单/合同：找"数量""条件""有效期"）→ 納品書（送货单：找"交货日期""数量确认"）→ 請求書（发票：找"金额""支付期限""支付方式"）→ 領収書（收据：找"支付凭证"）。图表阅读策略：先看图表标题和轴标签→把握整体趋势和对比关系→再阅读文章，将文字描述与图表数据对应。',
                },
                {
                  number: 2,
                  title: '业绩与财务关键指标',
                  content: '売上高（Sales Revenue，销售额）；利益（Profit，利润）——细分有営業利益（营业利润）、経常利益（经常利润）；経費（Expenses，费用）；損益分岐点（Break-even Point，使总收入和总成本相等所需的销售额）；固定費/変動費（Fixed/Variable Costs，固定/变动成本）。增长描述：「伸びる・増加する」（稳步）；「急増する・うなぎ登り」（急速）。停滞/下降：「頭打ち・横ばい」（停滞）；「減少する・落ち込む」（下降）。比较：「前期比～%増/減・～を上回る/下回る」。',
                },
              ],
            },
          },
        ],
      },
    ],
  },
};
